// =============================================================================
// VandieZ admin-web -- Dispute detail (/disputes/:disputeId)
// =============================================================================
// Renders the dispute payload + evidence images + parties (linked into
// the trip + the raising user's KYC review). Resolution form at the
// bottom accepts a resolution_action + notes + optional reject flag.
//
// On submission with resolution_action ∈ {refund_shipper, credit_driver}
// the backend writes a wallet_ledger pair atomically; UI just shows the
// new status + the refund_amount + the resolution_notes.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { disputesAdminApi, kycAdminApi } from "@/api/client";
import { useToast } from "@/lib/toast";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  DisputeRead,
  DisputeResolution,
  DisputeStatus,
} from "@/types/disputes";

const STATUS_PILL: Record<DisputeStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  under_review: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  rejected: "bg-muted text-muted-foreground",
};

const RESOLUTION_LABEL: Record<DisputeResolution, string> = {
  refund_shipper:
    "Refund shipper (credit shipper / debit driver by trip value)",
  credit_driver:
    "Credit driver (credit driver / debit shipper by trip value)",
  no_action: "No action (no money move; both parties at fault or unclear)",
};

function formatDate(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

interface EvidenceImageProps {
  fileId: string;
}

function EvidenceImage({ fileId }: EvidenceImageProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [url, setUrl] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (accessToken === null) {
      return;
    }
    const controller = new AbortController();
    const ref: { revoked: boolean; url: string | null } = {
      revoked: false,
      url: null,
    };
    void (async () => {
      try {
        const blob = await kycAdminApi.documentFile(
          accessToken,
          fileId,
          controller.signal,
        );
        ref.url = URL.createObjectURL(blob);
        if (!ref.revoked) {
          setUrl(ref.url);
        }
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setErrorMsg(
          e instanceof ApiError ? e.problem.detail : "Failed to load.",
        );
      }
    })();
    return () => {
      ref.revoked = true;
      controller.abort();
      if (ref.url !== null) {
        URL.revokeObjectURL(ref.url);
      }
    };
  }, [accessToken, fileId]);

  if (errorMsg !== null) {
    return (
      <div className="flex h-32 items-center justify-center rounded border bg-muted text-xs text-muted-foreground">
        {errorMsg}
      </div>
    );
  }
  if (url === null) {
    return (
      <div className="flex h-32 items-center justify-center rounded border bg-muted text-xs text-muted-foreground">
        <Loader2 className="mr-1 h-3 w-3 animate-spin" /> Loading
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt="Evidence"
        className="h-32 w-full rounded border object-cover"
      />
    </a>
  );
}

export function DisputeDetailPage(): React.JSX.Element {
  const { disputeId } = useParams<{ disputeId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const toast = useToast();
  const [dispute, setDispute] = useState<DisputeRead | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] =
    useState<DisputeResolution | null>(null);
  const [reject, setReject] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null || disputeId === undefined) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await disputesAdminApi.detail(
          accessToken,
          disputeId,
          signal,
        );
        setDispute(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, disputeId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const submit = async () => {
    if (
      accessToken === null ||
      disputeId === undefined ||
      pendingAction === null
    ) {
      return;
    }
    if (notes.trim().length < 10) {
      setSubmitError("Notes must be at least 10 characters.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await disputesAdminApi.decide(accessToken, disputeId, {
        resolution_action: pendingAction,
        notes: notes.trim(),
        reject,
      });
      setDispute(res);
      const flashMsg =
        res.status === "resolved"
          ? `Resolved with ${pendingAction.replace(/_/g, " ")}.`
          : "Marked rejected.";
      setFlash(flashMsg);
      toast.success(flashMsg);
      setPendingAction(null);
      setNotes("");
      setReject(false);
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isTerminal =
    dispute !== null &&
    (dispute.status === "resolved" || dispute.status === "rejected");

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dispute review"
        description="Pick a resolution + write notes the parties will see."
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { navigate("/disputes"); }}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Queue
          </Button>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {flash !== null && (
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Decision applied</AlertTitle>
            <AlertDescription>{flash}</AlertDescription>
          </Alert>
        )}

        {loading && dispute === null ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-2/3" />
          </div>
        ) : dispute !== null ? (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <CardTitle className="capitalize">
                      {dispute.category.replace(/_/g, " ")}
                    </CardTitle>
                    <CardDescription>
                      Raised{" "}
                      <span className="capitalize">
                        {dispute.raised_by_role}
                      </span>{" "}
                      · {formatDate(dispute.raised_at)} · trip{" "}
                      <Link
                        to={`/trips/${dispute.trip_id}`}
                        className="font-mono hover:underline"
                      >
                        {dispute.trip_id.slice(0, 8)}…
                      </Link>
                    </CardDescription>
                  </div>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                      STATUS_PILL[dispute.status],
                    )}
                  >
                    {dispute.status.replace(/_/g, " ")}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border bg-background p-3 text-sm whitespace-pre-wrap">
                  {dispute.description}
                </div>
              </CardContent>
            </Card>

            {dispute.evidence_files.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Evidence ({dispute.evidence_files.length})
                  </CardTitle>
                  <CardDescription>Click to open full size.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {dispute.evidence_files.map((f) => (
                      <EvidenceImage key={f.id} fileId={f.id} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {isTerminal ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Resolution</CardTitle>
                  <CardDescription>
                    Resolved {formatDate(dispute.resolved_at)} ·{" "}
                    {dispute.resolution_action !== null
                      ? RESOLUTION_LABEL[dispute.resolution_action]
                      : "—"}{" "}
                    · refund amount ₹{dispute.refund_amount}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border bg-background p-3 text-sm whitespace-pre-wrap">
                    {dispute.resolution_notes ?? "—"}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Decide</CardTitle>
                  <CardDescription>
                    Pick a resolution + write notes the parties will see.
                    Notes are required (min 10 characters).
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    {(
                      ["refund_shipper", "credit_driver", "no_action"] as DisputeResolution[]
                    ).map((opt) => (
                      <label
                        key={opt}
                        className={cn(
                          "flex cursor-pointer items-start gap-2 rounded border p-2 text-sm",
                          pendingAction === opt
                            ? "border-primary bg-primary/5"
                            : "border-input",
                        )}
                      >
                        <input
                          type="radio"
                          name="resolution"
                          className="mt-1"
                          checked={pendingAction === opt}
                          onChange={() => {
                            setPendingAction(opt);
                          }}
                          disabled={submitting}
                        />
                        <span>{RESOLUTION_LABEL[opt]}</span>
                      </label>
                    ))}
                  </div>
                  <div>
                    <Label htmlFor="dispute-notes">Notes (visible to both parties)</Label>
                    <Textarea
                      id="dispute-notes"
                      value={notes}
                      onChange={(e) => {
                        setNotes(e.target.value);
                      }}
                      maxLength={2000}
                      disabled={submitting}
                      placeholder="Why you picked this resolution. Both shipper and driver will read this."
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={reject}
                      onChange={(e) => {
                        setReject(e.target.checked);
                      }}
                      disabled={submitting}
                    />
                    Mark <span className="font-medium">rejected</span> instead
                    of resolved (no money move; complaint found unfounded).
                  </label>
                  {submitError !== null && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{submitError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="flex justify-end">
                    <Button
                      onClick={() => void submit()}
                      disabled={submitting || pendingAction === null}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          Submitting…
                        </>
                      ) : (
                        "Confirm decision"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
    </div>
  );
}
